import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Types
interface User {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  email_verified: boolean;
  phone_verified: boolean;
}

interface SessionData {
  status: string;
  user_id?: string;
  username?: string;
  user?: User;
  created_at?: string;
  expires_at?: string;
  metadata?: Record<string, any>;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (user: User) => void;
  logout: () => Promise<void>;
  checkAuthStatus: () => Promise<void>;
}

interface AuthProviderProps {
  children: ReactNode;
}

// Create the context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Custom hook to use the auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth Provider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check authentication status on app load and when needed
  const checkAuthStatus = async (): Promise<void> => {
    try {
      const response = await fetch('/api/session', {
        method: 'GET',
        credentials: 'include', // Include cookies
      });

      if (response.ok) {
        const data: SessionData = await response.json();
        
        if (data.status === 'authenticated' && data.user) {
          // Session is valid and includes full user data
          setUser(data.user);
        } else if (data.status === 'authenticated' && data.user_id) {
          // Fallback: Session is valid but no full user data
          const sessionUser: User = {
            user_id: data.user_id,
            first_name: '',
            last_name: '',
            email: data.username || '',
            email_verified: false,
            phone_verified: false,
          };
          setUser(sessionUser);
        } else {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Login function - call this after successful login
  const login = (userData: User): void => {
    setUser(userData);
  };

  // Logout function
  const logout = async (): Promise<void> => {
    try {
      const response = await fetch('/api/session', {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        setUser(null);
      } else {
        // Even if logout fails on backend, clear local state
        console.error('Logout failed on backend, but clearing local state');
        setUser(null);
      }
    } catch (error) {
      console.error('Error during logout:', error);
      // Clear local state even if network request fails
      setUser(null);
    }
  };

  // Check auth status on component mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated: user !== null,
    isLoading,
    login,
    logout,
    checkAuthStatus,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
